import React, { useState } from 'react';
import { Box, Dialog, Typography } from '@mui/material';
import { ctaButtonSx, secondaryButtonSx } from '../../../../../components/ui/Buttons/buttonStyles';
import CloseIcon from '@mui/icons-material/Close';
import LessonDescriptionEditor from '../../Program/components/program tabs/settings components/LessonDescriptionEditor';

interface EditLessonModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (title: string, content: string) => void;
  lessonTitle: string;
  lessonContent: string;
}

const EditLessonModal: React.FC<EditLessonModalProps> = ({ open, onClose, onSave, lessonTitle, lessonContent }) => {
  const [title, setTitle] = useState(lessonTitle);
  const [content, setContent] = useState(lessonContent);

  // Re-seed from props the moment the dialog opens — during render, not in an
  // effect. This component stays mounted across lesson switches (the parents
  // toggle `open`), so an effect would let the editor mount with the PREVIOUS
  // lesson's copy for one frame and then re-hydrate (wrong content flashing,
  // cursor jumping to the end). Adjusting state during render commits the right
  // copy before the editor ever mounts. The editor hydrates its `value`
  // reliably on mount, so no blank-then-delay workaround is needed either.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setTitle(lessonTitle);
      setContent(lessonContent);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          background: '#1A1A1A',
          borderRadius: '12px',
          border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
          p: 3,
          maxHeight: '90vh'
        }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 600, fontFamily: 'Inter', color: 'var(--Text-text-primary, #FDFDFD)' }}>
          Edit Lesson Body
        </Typography>
        <Box component="button" onClick={onClose} sx={{ background: 'none', border: 'none', cursor: 'pointer', p: 0.5 }}>
          <CloseIcon sx={{ color: 'var(--Text-text-secondary, #B6B6B6)', fontSize: 20 }} />
        </Box>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 13, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)', mb: 0.5 }}>
          Lesson title
        </Typography>
        <Box
          component="input"
          value={title}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
          placeholder="Lesson title"
          sx={{
            width: '100%', background: '#0F0F0F',
            border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
            borderRadius: '8px', p: 1.5, color: 'var(--Text-text-primary, #FDFDFD)',
            fontFamily: 'Inter', fontSize: 14, outline: 'none',
            '&:focus': { borderColor: 'var(--Stroke-highlight, #F79420)' }
          }}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 13, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)', mb: 0.5 }}>
          Lesson Body
        </Typography>
        <Box sx={{
          background: '#0F0F0F',
          border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
          borderRadius: '8px',
          overflow: 'hidden',
          '& .wrapperClassName': { border: 'none !important', background: 'transparent !important' },
          '& .toolBarStyle, & .toolBarStyleTask': {
            background: '#1A1A1A !important',
            borderBottom: '1px solid var(--Stroke-Border-border-dim, #2A2A2A) !important',
            borderTop: 'none !important',
            borderLeft: 'none !important',
            borderRight: 'none !important',
            mb: 0
          },
          '& .aimd-lesson-editor, & .rdw-editor-main': {
            minHeight: 300, maxHeight: 450, overflowY: 'auto',
            p: 2, color: '#FDFDFD !important',
            fontFamily: 'Inter', fontSize: 14, lineHeight: '24px'
          },
          '& .public-DraftStyleDefault-block': { color: '#FDFDFD' },
          '& .public-DraftEditorPlaceholder-root': { color: '#868686' }
        }}>
          {open && (
            <LessonDescriptionEditor
              key={`editor-${lessonTitle}-${open}`}
              value={content}
              onChange={setContent}
              placeholder="Write your Lesson Body here..."
              editorClassName="aimd-lesson-editor"
              wrapperClassName="wrapperClassName p-0 b-0"
              toolbarClassName="toolBarStyle toolBarStyleTask"
            />
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Box
          component="button"
          onClick={onClose}
          sx={{ ...secondaryButtonSx, cursor: 'pointer' }}
        >
          Cancel
        </Box>
        <Box
          component="button"
          onClick={() => onSave(title, content)}
          sx={{ ...ctaButtonSx, border: 'none', cursor: 'pointer' }}
        >
          Save Changes
        </Box>
      </Box>
    </Dialog>
  );
};

export default EditLessonModal;
